import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { JiraProductDiscoveryLogo } from '@atlaskit/logo';
import { Box, xcss } from '@atlaskit/primitives';
import { N800 } from '@atlaskit/theme/colors';
import { token, useThemeObserver } from '@atlaskit/tokens';
// eslint-disable-next-line jira/import/no-parent-imports
import JpdHeader from '../assets/jpd-header.svg';

export type JiraYourWorkNextBestActionMessageProps = {
	onTryNowClick: () => Promise<void>;
	onLearnMoreClick: () => Promise<void>;
	AdControls: React.FC;
	onDismissCrossflowMessage: () => void;
	isSiteAdmin: boolean;
};

export const JiraYourWorkNextBestActionMessage = ({
	onTryNowClick,
	onLearnMoreClick,
	AdControls,
	isSiteAdmin,
}: JiraYourWorkNextBestActionMessageProps) => {
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';
	return (
		<MessageWrapper>
			<MessageHeader isDarkMode={isDarkMode}>
				<img alt="Jira Product Discovery" src={JpdHeader} />
				<IconWrapper>
					<AdControls />
				</IconWrapper>
			</MessageHeader>
			<Box xcss={messageContentStyles}>
				<JiraProductDiscoveryLogo appearance="brand" size="small" />
				<MessageTitle>Build right features, the right way</MessageTitle>
				<MessageDescription>
					Prioritize your ideas then easily move them into delivery, without losing any details on
					the way.
				</MessageDescription>
				<Box xcss={messageFooterStyles}>
					<StyledButton onClick={onTryNowClick}>
						{isSiteAdmin ? 'Try it now' : 'Request to try'}
					</StyledButton>
					<Button onClick={onLearnMoreClick} appearance="subtle">
						Learn more
					</Button>
				</Box>
			</Box>
		</MessageWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageWrapper = styled.div({
	position: 'relative',
	width: '360px',
	height: '300px',
	backgroundColor: token('color.background.input', '#FFFFFF'),
	borderRadius: '3px',
	marginBottom: token('space.200', '16px'),
	boxShadow: token(
		'elevation.shadow.overflow',
		'0px 0px 8px 0px rgba(9, 30, 66, 0.16), 0px 0px 1px 0px rgba(9, 30, 66, 0.12)',
	),
});

const messageFooterStyles = xcss({
	marginTop: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)({
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageDescription = styled.div({
	color: token('color.text.subtle', N800),
	marginTop: token('space.100', '8px'),
	fontSize: '14px',
	fontStyle: 'normal',
	fontWeight: 400,
	lineHeight: '20px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageTitle = styled.div({
	color: token('color.text', '#172B4D'),
	width: '328px',
	fontSize: '20px',
	fontStyle: 'normal',
	fontWeight: 500,
	lineHeight: '24px',
});

const messageContentStyles = xcss({
	padding: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageHeader = styled.div<{ isDarkMode: boolean }>({
	height: '122px',
	borderRadius: '3px 3px 0 0',
	justifyContent: 'flex-start',
	alignItems: 'center',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: ({ isDarkMode }) => (isDarkMode ? '#09326C' : '#CCE0FF'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	position: 'absolute',
	top: token('space.150', '12px'),
	right: token('space.150', '12px'),
});
